<script setup lang="ts">
import GbButton from '@/components/GbButton.vue'
import Dialog from 'primevue/dialog'
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper.vue'
import ResourceDownloadButton from '@/resources/ResourceDownloadButton.vue'
import { Link } from '@/resources/resource_types'

const props = withDefaults(
  defineProps<{
    uuid: string
    title: string
    type?: string
    downloadLinks?: Link[]
    screenshots?: string[]
    numPages?: number
    googleDriveLinks?: Link[]
    language?: string
  }>(),
  {
    type: '',
    downloadLinks: () => [],
    screenshots: () => [],
    numPages: 0,
    googleDriveLinks: () => [],
    language: ''
  }
)

const visible = ref(true)
const keyForScreenshotIndex = (index: number) => {
  return `${props.uuid}-${index}`
}
const altForScreenshotIndex = (index: number) => {
  return `${props.title}, preview page ${index + 1} of ${props.screenshots.length}`
}
const longDescriptionId = computed(() => `${props.uuid}-preview-description`)
const hasDownload = computed(
  () => props.downloadLinks.length > 0 || props.googleDriveLinks.length > 0
)
const longDescription = computed(() => {
  const pageImages =
    props.screenshots.length === 1 ? '1 page image' : `${props.screenshots.length} page images`
  const fullLength = props.numPages > 0 ? `, which is ${props.numPages} pages long` : ''
  const readableFormat = hasDownload.value
    ? 'Use the Download button at the top of this preview to open the full resource in a readable format.'
    : 'This resource has no download, so there is no readable version of these pages.'
  return `This preview shows ${pageImages} from the resource "${props.title}"${fullLength}. The pages are pictures of the printed resource, so a screen reader cannot read the text inside them. ${readableFormat}`
})
const { isAuthenticated, isTrial, isLicenseExpired } = storeToRefs(useUserStore())
const imageRefs = ref<HTMLElement[]>([])

const previewImageTransformations = computed(() => {
  // https://docs.imagekit.io/features/image-transformations/overlay-using-layers#add-text-over-image
  const watermarkTransformations =
    'l-text,i-GOALBOOK PREVIEW,ff-Lato,co-00769F,bg-ddddddd,w-400,al-7,pa-30_20,r-5,fs-40,l-end'
  return !isAuthenticated.value || isTrial.value || isLicenseExpired.value
    ? { tr: `w-500,${watermarkTransformations}` }
    : { tr: 'w-500' }
})

const advancePage = (steps: number) => {
  // Grab the screenshot element bounding boxes
  const screenshotBoundingBoxes: DOMRect[] = []
  for (let i = 0; i < imageRefs.value.length; i++) {
    screenshotBoundingBoxes.push(imageRefs.value[i].getBoundingClientRect())
  }

  // Figure out which screenshot index should be the previous and next screenshots
  let prevIndex = 0
  let nextIndex = 0
  const topPadding = 80
  for (let index = 0; index < screenshotBoundingBoxes.length; index++) {
    // Currently viewing the last screenshot
    if (index == screenshotBoundingBoxes.length - 1) {
      if (screenshotBoundingBoxes[index].top < topPadding) {
        prevIndex = index
      } else {
        prevIndex = index - 1
      }
      nextIndex = index
      break
    }
    // Currently screenshot is exactly aligned with top
    else if (Math.floor(screenshotBoundingBoxes[index].top) === topPadding) {
      prevIndex = index - 1
      nextIndex = index + 1
      break
    }
    // Somewhere in between two screen shots
    else if (
      screenshotBoundingBoxes[index].top < topPadding &&
      Math.floor(screenshotBoundingBoxes[index + 1].top) > topPadding
    ) {
      prevIndex = index
      nextIndex = index + 1
      break
    }
  }

  // From current previous go to next screen shot indices
  // based on step direction
  if (steps < 0) {
    const indexToView = Math.max(prevIndex, 0)
    imageRefs.value[indexToView].scrollIntoView({
      behavior: 'smooth'
    })
  } else if (steps > 0) {
    const indexToView = Math.min(nextIndex, screenshotBoundingBoxes.length - 1)
    imageRefs.value[indexToView].scrollIntoView({ behavior: 'smooth' })
  }
}
</script>

<template>
  <Dialog
    :aria-labelledby="uuid + '-resource-modal-title'"
    v-model:visible="visible"
    :dismissableMask="true"
    modal
    :pt="{
      root: 'border-none h-full w-full',
      mask: {
        style: 'backdrop-filter: blur(2px)'
      }
    }"
    :blockScroll="true"
  >
    <template #container="{ closeCallback }">
      <div class="fixed left-0 top-0 h-full w-full overflow-auto">
        <div class="fixed left-0 top-0 z-20 w-full bg-black p-4">
          <div class="container flex justify-between align-middle">
            <span class="self-center text-white" :id="uuid + '-resource-modal-title'">{{
              title
            }}</span>
            <ResourceDownloadButton
              :resource="{
                uuid,
                type,
                title,
                download_links: downloadLinks,
                google_drive_links: googleDriveLinks,
                language
              }"
            />
            <GbButton
              :autofocus="true"
              label="Close Preview"
              @click="closeCallback"
              text
              class="text-md text-white"
              size="small"
            >
              <span class="material-icons-round">close</span> Close Preview
            </GbButton>
          </div>
        </div>
        <template v-if="screenshots.length > 1">
          <a
            class="fixed left-5 top-1/2 z-20 cursor-pointer rounded-full bg-gray-1000 px-2 py-1 font-bold text-white hover:bg-gray-700 focus:bg-gray-700 focus:outline focus:outline-offset-2 focus:outline-gray-1000"
            @click="advancePage(-1)"
            @keyup.enter="advancePage(-1)"
            tabindex="0"
          >
            <span class="material-icons-round mt-1 text-2xl">chevron_left</span>
          </a>
          <a
            class="fixed right-5 top-1/2 z-20 cursor-pointer rounded-full bg-gray-1000 px-2 py-1 font-bold text-white hover:bg-gray-700 focus:bg-gray-700 focus:outline focus:outline-offset-2 focus:outline-gray-1000"
            @click="advancePage(1)"
            @keyup.enter="advancePage(1)"
            tabindex="0"
          >
            <span class="material-icons-round mt-1 text-2xl">chevron_right</span>
          </a>
        </template>
        <p class="sr-only" :id="longDescriptionId">{{ longDescription }}</p>
        <div class="absolute inset-0 z-10 inline-flex h-full flex-col gap-5 pt-20">
          <div
            v-for="(img, index) in screenshots"
            :key="keyForScreenshotIndex(index)"
            :id="keyForScreenshotIndex(index)"
            ref="imageRefs"
            class="static mx-auto scroll-mt-20 last:pb-96"
          >
            <IKImageS3Wrapper
              class="resource_thumbnail_image"
              :aws-url="img"
              :alt="altForScreenshotIndex(index)"
              :aria-describedby="longDescriptionId"
              :query-parameters="previewImageTransformations"
            />
          </div>
        </div>
        <div v-if="numPages > 0" class="fixed bottom-12 z-20 w-full">
          <div
            class="gap mx-auto flex w-60 flex-col items-center justify-center rounded-full bg-gray-1000 p-4 text-white opacity-80"
          >
            <span>Full resource contains</span>
            <span class="font-bold">{{ numPages }} pages</span>
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>
